import React, { useState, useEffect } from 'react';
import {
    List,
    Datagrid,
    DatagridConfigurable,
    TextField,
    DateField,
    useTranslate,
    Filter,
    FilterLiveSearch,
    TopToolbar,
    SelectColumnsButton,
    useRecordContext,
    useRedirect,
    NumberField,
    BooleanField,
    useListContext,
    FunctionField,
    useNotify,
    Button as RaButton
} from 'react-admin';
import { Chip, Card, CardContent, Stack, Box, Typography, FormControl, InputLabel, Select, MenuItem, Divider, Checkbox, ListItemText, Tooltip, Button as MuiButton, TextField as MuiTextField } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import ArchiveIcon from '@mui/icons-material/Archive';
import { styled } from '@mui/material/styles';

// Custom Bidder Info Field - Name, NIP, Email in one column
const BidderInfoField = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    
    if (!record) return null;
    
    const handleNameClick = (e) => {
        e.stopPropagation();
        if (record.bidder_id) {
            redirect('show', 'bidders', record.bidder_id);
        }
    };
    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 160 }}>
            <Typography
                component="a"
                onClick={handleNameClick}
                sx={{
                    color: 'primary.main',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    '&:hover': {
                        color: 'primary.dark',
                    }
                }}
            >
                {record.bidder_name || '-'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                NIP: {record.bidder_tax_number || '-'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', wordBreak: 'break-all' }}>
                Email: {record.bidder_email || '-'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Ryzyko: {record.bidder_financial_risk_rating ? (
                    <Tooltip 
                        title={record.bidder_financial_risk_description || ''}
                        arrow
                        placement="top"
                    >
                        <Chip 
                            label={record.bidder_financial_risk_rating}
                            color={record.bidder_financial_risk_color || 'default'}
                            size="small"
                            sx={{ fontSize: '0.65rem', height: '18px', ml: 0.5, cursor: 'help' }}
                        />
                    </Tooltip>
                ) : (
                    <span style={{ fontStyle: 'italic' }}>Brak danych</span>
                )}
            </Typography>
        </Box>
    );
};

// Combined Tender Info Field - Title, Notice Number, Wadium and NWU in one column
const TenderInfoField = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    
    if (!record) return null;
    
    const handleTitleClick = (e) => {
        e.stopPropagation();
        if (record.tender_id) {
            redirect('show', 'tenders', record.tender_id);
        }
    };
    
    // Helper function to render Wadium/NWU status
    const renderStatusChip = (value, label) => {
        if (value === null || value === undefined) {
            return <Chip label="-" color="default" size="small" sx={{ fontSize: '0.7rem', height: '20px' }} />;
        }
        if (Number(value) === 0) {
            return <Chip label={translate('ra.boolean.false', 'Nie')} color="default" size="small" sx={{ fontSize: '0.7rem', height: '20px' }} />;
        }
        if (Number(value) > 0) {
            return <Chip label={translate('ra.boolean.true', 'Tak')} color="success" size="small" sx={{ fontSize: '0.7rem', height: '20px' }} />;
        }
        return <Chip label="-" color="default" size="small" sx={{ fontSize: '0.7rem', height: '20px' }} />;
    };
    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 200 }}>
            <Typography
                component="a"
                onClick={handleTitleClick}
                sx={{
                    color: 'primary.main',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    lineHeight: 1.2,
                    '&:hover': {
                        color: 'primary.dark',
                    }
                }}
            >
                {record.tender_title || '-'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {record.tender_notice_number || '-'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                        {translate('resources.tenders.fields.bid_bond', 'Wadium')}:
                    </Typography>
                    {renderStatusChip(record.tender_bid_bond)}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                        {translate('resources.tenders.fields.nwu', 'NWU')}:
                    </Typography>
                    {renderStatusChip(record.tender_nwu)}
                </Box>
            </Box>
        </Box>
    );
};

// Workflow Info Field - Shows archived status and last completed step
const WorkflowInfoField = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    
    if (!record) return null;
    
    // Get workflow status - for archived offers it should always be 'archived'
    const getWorkflowStatus = () => {
        if (!record.workflow_status) {
            return { color: 'default', label: 'Brak statusu' };
        }
        
        const status = record.workflow_status;
        switch (status) {
            case 'archived':
                return { color: 'default', label: 'Zarchiwizowany' };
            case 'failed':
                return { color: 'error', label: 'Nieudany' };
            case 'running':
                return { color: 'warning', label: 'W trakcie' };
            case 'done':
                return { color: 'success', label: 'Zakończony' };
            case 'pending':
                return { color: 'info', label: 'Oczekujący' };
            case 'waiting':
                return { color: 'warning', label: 'Oczekuje' };
            default:
                return { color: 'default', label: status || '-' };
        }
    };
    
    const statusInfo = getWorkflowStatus();
    const currentBlock = record?.workflow_current_block;
    
    // Parse log messages and get only the last one
    const getLastLogMessage = () => {
        if (!record?.workflow_log_message) return null;
        
        let messages = null;
        
        if (Array.isArray(record.workflow_log_message)) {
            messages = record.workflow_log_message;
        } else {
            try {
                const parsed = JSON.parse(record.workflow_log_message);
                if (Array.isArray(parsed)) {
                    messages = parsed;
                } else {
                    messages = [String(parsed)];
                }
            } catch (e) {
                messages = [String(record.workflow_log_message)];
            }
        }
        
        // Return only the last message from the array
        if (messages && messages.length > 0) {
            return messages[messages.length - 1];
        }
        
        return null;
    };
    
    const lastLogMessage = getLastLogMessage();
    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: '250px', maxWidth: '350px' }}>
            {/* Status Chip */}
            <Chip 
                label={statusInfo.label} 
                color={statusInfo.color} 
                size="small"
                icon={<ArchiveIcon />}
            />
            
            {/* Last completed block - only show if we have it */}
            {currentBlock && (
                <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" sx={{ 
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                        mb: 0.25
                    }}>
                        Ostatni zakończony krok:
                    </Typography>
                    <Typography variant="body2" sx={{ 
                        fontSize: '0.875rem',
                        color: 'text.primary',
                        wordBreak: 'break-word',
                        lineHeight: 1.3
                    }}>
                        {currentBlock}
                    </Typography>
                </Box>
            )}
            
            {/* Log message - show reason for archiving (only last message) */}
            {lastLogMessage && (
                <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" sx={{ 
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                        mb: 0.25
                    }}>
                        Powód archiwizacji:
                    </Typography>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            fontSize: '0.8rem',
                            color: 'text.primary',
                            wordBreak: 'break-word',
                            lineHeight: 1.3
                        }}
                    >
                        {lastLogMessage}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

// Custom Offer Status Field component
const OfferStatusField = ({ source }) => {
    const record = useRecordContext();
    if (!record || !record[source]) return null;
    
    const status = record[source];
    let color = 'default';
    let label = status;
    
    switch (status) {
        case 'LEAD':
            color = 'success';
            label = 'Lead';
            break;
        case 'REJECTED':
            color = 'error';
            label = 'Odrzucony';
            break;
        case 'PARTICIPANT':
            color = 'info';
            label = 'Uczestnik';
            break;
        default:
            color = 'default';
            label = status || '-';
    }
    
    return <Chip label={label} color={color} size="small" />;
};

const StyledList = styled(List)({
  '& .MuiPaper-root': {
    padding: '12px 16px !important',
    border: '1px solid #e0e0e0',
    backgroundColor: '#fff',
  },
  '& .MuiToolbar-root': {
    minHeight: '40px !important',
    paddingLeft: 0,
    paddingRight: 0,
    marginBottom: '0px',
  }
});

// Custom export: fetches from dedicated export endpoint (up to 2000 records, no pagination)
const ExportArchivedButton = () => {
  const { filterValues, sort } = useListContext();
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const translate = useTranslate();

  const handleExport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filterValues || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, Array.isArray(value) ? value.join(',') : value);
        }
      });
      if (sort?.field) {
        params.append('_sort', sort.field);
        params.append('_order', sort.order?.toLowerCase() || 'desc');
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/bidders/archived/export/?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (!response.ok) {
        notify(result.error || 'Export failed', { type: 'error' });
        return;
      }

      const data = result.data;
      if (!data || data.length === 0) {
        notify(translate('ra.message.no_data', 'Brak danych do eksportu'), { type: 'warning' });
        return;
      }

      if (result.warning) {
        notify(result.warning, { type: 'warning' });
      }

      const flattenedData = data.map(record => ({
        ID: record.id ?? '',
        'Nazwa oferenta': record.bidder_name ?? '',
        NIP: record.bidder_tax_number ?? '',
        Email: record.bidder_email ?? '',
        'Numer ogłoszenia': record.tender_notice_number ?? '',
        'Tytuł przetargu': record.tender_title ?? '',
        'Termin składania ofert': record.tender_deadline ?? '',
        'Wartość oferty': record.offer_value ?? '',
        'Data oferty': record.offer_date ?? '',
        Status: record.status ?? '',
        'Status procesu': record.workflow_status ?? '',
        'Ostatni krok': record.workflow_current_block ?? '',
      }));

      const worksheet = XLSX.utils.json_to_sheet(flattenedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Oferenci zarchiwizowani');
      XLSX.writeFile(workbook, `archived_bidders_export_${new Date().toISOString().split('T')[0]}.xlsx`);

      notify(`Eksportowano ${data.length} rekordów`, { type: 'success' });
    } catch (err) {
      console.error('Export error:', err);
      notify('Błąd podczas eksportu', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <RaButton
      label={loading ? translate('resources.bidders.export.exporting', 'Eksportowanie...') : translate('ra.action.export', 'Eksportuj')}
      onClick={handleExport}
      disabled={loading}
      startIcon={<FileDownloadIcon />}
      sx={{
        backgroundColor: '#E31837',
        color: '#ffffff',
        '&:hover': { backgroundColor: '#C41430' },
        '& .MuiSvgIcon-root': { color: '#ffffff' },
      }}
    />
  );
};

const ListActions = () => {
  return (
    <TopToolbar>
      <SelectColumnsButton />
      <ExportArchivedButton />
    </TopToolbar>
  );
};

const BidderOfferArchivedFilterSidebar = () => {
  const translate = useTranslate();
  const { setFilters, filterValues } = useListContext();
  
  const [filterKey, setFilterKey] = useState(Date.now());
  const [nwu, setNwu] = useState(filterValues.is_nwu || '');
  const [wadium, setWadium] = useState(filterValues.is_wadium || '');
  const [archivedAtFrom, setArchivedAtFrom] = useState(filterValues.archived_at_from || '');
  const [archivedAtTo, setArchivedAtTo] = useState(filterValues.archived_at_to || '');
  const [financialRisk, setFinancialRisk] = useState(() => {
    if (filterValues.financial_risk_rating) {
      return filterValues.financial_risk_rating.split(',').map(v => v.trim()).filter(v => v);
    }
    return [];
  });

  useEffect(() => {
    if (filterValues.financial_risk_rating) {
      const values = filterValues.financial_risk_rating.split(',').map(v => v.trim()).filter(v => v);
      setFinancialRisk(values);
    } else {
      setFinancialRisk([]);
    }
  }, [filterValues.financial_risk_rating]);

  useEffect(() => {
    const hasAnyFilter = 
      filterValues.bidder_name || 
      filterValues.bidder_tax_number || 
      filterValues.bidder_email ||
      filterValues.bidder_registration_number ||
      filterValues.bidder_krs ||
      filterValues.bidder_pkd ||
      filterValues.bidder_city ||
      filterValues.bidder_municipality ||
      filterValues.bidder_province ||
      filterValues.tender_notice_number ||
      filterValues.tender_title ||
      filterValues.is_nwu ||
      filterValues.is_wadium ||
      filterValues.is_current_customer !== undefined ||
      filterValues.financial_risk_rating ||
      filterValues.archived_at_from ||
      filterValues.archived_at_to;
    
    if (!hasAnyFilter && Object.keys(filterValues).length === 0) {
      setFilterKey(Date.now());
    }
  }, [filterValues]);
  const getPolfundCustomerValue = () => {
    if (filterValues.is_current_customer === undefined || filterValues.is_current_customer === null) {
      return '';
    }
    return filterValues.is_current_customer === true || filterValues.is_current_customer === 'true' ? 'true' : 'false';
  };

  const handleNwuChange = (e) => {
    const value = e.target.value;
    setNwu(value);
    
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.is_nwu;
    } else {
      newFilters.is_nwu = value;
    }
    setFilters(newFilters, {});
  };

  const handleWadiumChange = (e) => {
    const value = e.target.value;
    setWadium(value);
    
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.is_wadium;
    } else {
      newFilters.is_wadium = value;
    }
    setFilters(newFilters, {});
  };

  const handlePolfundCustomerChange = (event) => {
    const value = event.target.value;
    const newFilters = { ...filterValues };
    
    if (value === '' || value === null || value === undefined) {
      delete newFilters.is_current_customer;
    } else {
      newFilters.is_current_customer = value === 'true';
    }
    
    setFilters(newFilters, {});
  };

  const handleFinancialRiskChange = (event) => {
    const value = event.target.value;
    const selectedValues = typeof value === 'string' ? value.split(',') : value;
    setFinancialRisk(selectedValues);
    
    const newFilters = { ...filterValues };
    if (!selectedValues || selectedValues.length === 0) {
      delete newFilters.financial_risk_rating;
    } else {
      newFilters.financial_risk_rating = selectedValues.join(',');
    }
    setFilters(newFilters, {});
  };

  const handleArchivedAtFromChange = (e) => {
    const value = e.target.value;
    setArchivedAtFrom(value);
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.archived_at_from;
    } else {
      newFilters.archived_at_from = value;
    }
    setFilters(newFilters, {});
  };

  const handleArchivedAtToChange = (e) => {
    const value = e.target.value;
    setArchivedAtTo(value);
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.archived_at_to;
    } else {
      newFilters.archived_at_to = value;
    }
    setFilters(newFilters, {});
  };

  const handleResetFilters = () => {
    setNwu('');
    setWadium('');
    setFinancialRisk([]);
    setArchivedAtFrom('');
    setArchivedAtTo('');
    
    setFilterKey(Date.now());
    
    requestAnimationFrame(() => {
      const allFilterFields = {
        bidder_name: '',
        bidder_tax_number: '',
        bidder_email: '',
        bidder_registration_number: '',
        bidder_krs: '',
        bidder_pkd: '',
        bidder_city: '',
        bidder_municipality: '',
        bidder_province: '',
        tender_notice_number: '',
        tender_title: '',
        is_nwu: '',
        is_wadium: '',
        is_current_customer: '',
        financial_risk_rating: '',
        archived_at_from: '',
        archived_at_to: '',
      };
      
      setFilters(allFilterFields, {});
      
      requestAnimationFrame(() => {
        setFilters({}, {});
      });
    });
  };

  return (
    <Card sx={{ order: -1, mr: 2, mt: 9, minWidth: 250, maxWidth: 300, boxShadow: 0, borderRadius: 2 }}>
      <CardContent>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mt: 1, mb: 0.5 }}>
            Filtry po oferenci
          </Typography>
          
          <FilterLiveSearch key={`bidder_name-${filterKey}`} source="bidder_name" label={translate('resources.bidders.fields.name', 'Nazwa oferenta')} />
          <FilterLiveSearch key={`bidder_tax_number-${filterKey}`} source="bidder_tax_number" label={translate('resources.bidders.fields.tax_number', 'NIP')} />
          <FilterLiveSearch key={`bidder_email-${filterKey}`} source="bidder_email" label="Email" />
          <FilterLiveSearch key={`bidder_registration_number-${filterKey}`} source="bidder_registration_number" label={translate('resources.bidders.fields.registration_number', 'REGON')} />
          <FilterLiveSearch key={`bidder_krs-${filterKey}`} source="bidder_krs" label={translate('resources.bidders.fields.krs', 'KRS')} />
          <FilterLiveSearch key={`bidder_pkd-${filterKey}`} source="bidder_pkd" label={translate('resources.bidders.fields.pkd', 'PKD')} />
          <FilterLiveSearch key={`bidder_city-${filterKey}`} source="bidder_city" label={translate('resources.bidders.fields.city', 'Miasto')} />
          <FilterLiveSearch key={`bidder_municipality-${filterKey}`} source="bidder_municipality" label={translate('resources.bidders.fields.municipality', 'Gmina')} />
          <FilterLiveSearch key={`bidder_province-${filterKey}`} source="bidder_province" label={translate('resources.bidders.fields.province', 'Województwo')} />
          
          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="polfund-customer-filter-label" sx={{ fontSize: '0.875rem' }}>
              Czy klient Polfund
            </InputLabel>
            <Select
              labelId="polfund-customer-filter-label"
              value={getPolfundCustomerValue()}
              onChange={handlePolfundCustomerChange}
              label="Czy klient Polfund"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">TAK</MenuItem>
              <MenuItem value="false">NIE</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="financial-risk-filter-label" sx={{ fontSize: '0.875rem' }}>
              Skala ryzyka finansowego
            </InputLabel>
            <Select
              labelId="financial-risk-filter-label"
              multiple
              value={financialRisk}
              onChange={handleFinancialRiskChange}
              label="Skala ryzyka finansowego"
              sx={{ fontSize: '0.875rem' }}
              renderValue={(selected) => {
                if (selected.length === 0) return '';
                if (selected.length === 1) {
                  const labels = {
                    '5': '5',
                    '4+': '4+',
                    '4': '4',
                    '4-': '4-',
                    '3+': '3+',
                    '3': '3',
                    '3-': '3-',
                    '2': '2',
                    '?': '?',
                    'none': 'Brak danych'
                  };
                  return labels[selected[0]] || selected[0];
                }
                return `${selected.length} wybrane`;
              }}
            >
              <MenuItem value="5">
                <Checkbox checked={financialRisk.indexOf('5') > -1} />
                <ListItemText primary="5 - Znikome ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="4+">
                <Checkbox checked={financialRisk.indexOf('4+') > -1} />
                <ListItemText primary="4+ - Minimalne ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="4">
                <Checkbox checked={financialRisk.indexOf('4') > -1} />
                <ListItemText primary="4 - Bardzo niskie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="4-">
                <Checkbox checked={financialRisk.indexOf('4-') > -1} />
                <ListItemText primary="4- - Niskie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="3+">
                <Checkbox checked={financialRisk.indexOf('3+') > -1} />
                <ListItemText primary="3+ - Średnie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="3">
                <Checkbox checked={financialRisk.indexOf('3') > -1} />
                <ListItemText primary="3 - Wysokie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="3-">
                <Checkbox checked={financialRisk.indexOf('3-') > -1} />
                <ListItemText primary="3- - Bardzo wysokie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="2">
                <Checkbox checked={financialRisk.indexOf('2') > -1} />
                <ListItemText primary="2 - Krytycznie wysokie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="?">
                <Checkbox checked={financialRisk.indexOf('?') > -1} />
                <ListItemText primary="? - Brak danych do oceny finansowej" />
              </MenuItem>
              <MenuItem value="none">
                <Checkbox checked={financialRisk.indexOf('none') > -1} />
                <ListItemText primary="Brak danych" />
              </MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
            Filtry po przetargu
          </Typography>
          
          <FilterLiveSearch key={`tender_notice_number-${filterKey}`} source="tender_notice_number" label="Numer ogłoszenia" />
          <FilterLiveSearch key={`tender_title-${filterKey}`} source="tender_title" label="Tytuł przetargu" />
          
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="nwu-filter-label" sx={{ fontSize: '0.875rem' }}>
              {translate('resources.tenders.fields.is_nwu', 'Czy jest NWU')}
            </InputLabel>
            <Select
              labelId="nwu-filter-label"
              value={nwu}
              onChange={handleNwuChange}
              label={translate('resources.tenders.fields.is_nwu', 'Czy jest NWU')}
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">{translate('ra.boolean.true', 'Tak')}</MenuItem>
              <MenuItem value="false">{translate('ra.boolean.false', 'Nie')}</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="wadium-filter-label" sx={{ fontSize: '0.875rem' }}>
              {translate('resources.tenders.fields.is_wadium', 'Czy jest Wadium')}
            </InputLabel>
            <Select
              labelId="wadium-filter-label"
              value={wadium}
              onChange={handleWadiumChange}
              label={translate('resources.tenders.fields.is_wadium', 'Czy jest Wadium')}
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">{translate('ra.boolean.true', 'Tak')}</MenuItem>
              <MenuItem value="false">{translate('ra.boolean.false', 'Nie')}</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mt: 1, mb: 0.5 }}>
            Data archiwizacji
          </Typography>
          <MuiTextField
            key={`archived_at_from-${filterKey}`}
            label="Od daty"
            type="date"
            size="small"
            fullWidth
            value={archivedAtFrom}
            onChange={handleArchivedAtFromChange}
            InputLabelProps={{ shrink: true }}
            sx={{ fontSize: '0.875rem' }}
          />
          <MuiTextField
            key={`archived_at_to-${filterKey}`}
            label="Do daty"
            type="date"
            size="small"
            fullWidth
            value={archivedAtTo}
            onChange={handleArchivedAtToChange}
            InputLabelProps={{ shrink: true }}
            sx={{ fontSize: '0.875rem' }}
          />

          <MuiButton
            variant="outlined"
            size="small"
            onClick={handleResetFilters}
            sx={{ mt: 3, mb: 1 }}
            fullWidth
          >
            Wyczyść filtry
          </MuiButton>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const BidderOfferArchivedList = props => {
    const translate = useTranslate();
    return (
        <StyledList 
            {...props}
            actions={<ListActions />}
            perPage={25}
            sort={{ field: 'offer_date', order: 'DESC' }}
            aside={<BidderOfferArchivedFilterSidebar />}
            title={translate('resources.bidders.archived', 'Oferenci zarchiwizowani')}
        >
            <div style={{ width: '100%', overflowX: 'auto' }}>
                <DatagridConfigurable 
                    sx={{ 
                        minWidth: 'auto',
                        '& .MuiTableCell-root': {
                            padding: '8px 12px !important',
                            verticalAlign: 'top !important',
                            minHeight: '60px !important',
                            height: 'auto !important',
                            overflow: 'visible !important'
                        },
                        '& .MuiTableRow-root': {
                            minHeight: '60px !important',
                            height: 'auto !important'
                        },
                        '& .column-id': { minWidth: '60px', maxWidth: '60px' },
                        '& .column-bidder_info': { minWidth: '180px', maxWidth: '220px' },
                        '& .column-tender_info': { minWidth: '220px', maxWidth: '280px' },
                        '& .column-tender_deadline': { minWidth: '140px', maxWidth: '140px' },
                        '& .column-offer_value': { minWidth: '120px', maxWidth: '140px' },
                        '& .column-status': { minWidth: '100px', maxWidth: '120px' },
                        '& .column-offer_date': { minWidth: '140px', maxWidth: '140px' },
                        '& .column-workflow_archived_at': { minWidth: '140px', maxWidth: '140px' },
                        '& .column-workflow_info': { minWidth: '250px', maxWidth: '350px' }
                    }}
                    rowClick={false}
                    bulkActionButtons={false}
                    omit={[
                        'bidder_id',
                        'bidder_name',
                        'bidder_tax_number', 
                        'bidder_email',
                        'bidder_registration_number',
                        'bidder_krs',
                        'bidder_pkd',
                        'bidder_city',
                        'bidder_municipality',
                        'bidder_province',
                        'tender_id',
                        'tender_notice_number',
                        'tender_title',
                        'tender_value',
                        'notes',
                        'is_lead',
                        'workflow_status',
                        'workflow_current_block',
                        'workflow_error_message',
                        'workflow_log_message'
                    ]}
                >
                    <TextField source="id" label="ID" />
                    <BidderInfoField source="bidder_info" label="Oferent" />
                    <TenderInfoField source="tender_info" label="Przetarg" />
                    <DateField source="tender_deadline" label="Termin składania ofert" showTime />
                    <NumberField source="offer_value" label="Wartość oferty" options={{ style: 'currency', currency: 'PLN' }} />
                    <OfferStatusField source="status" label="Status" />
                    <DateField source="offer_date" label="Data oferty" showTime />
                    <DateField source="workflow_archived_at" label="Data archiwizacji" showTime />
                    <FunctionField
                        source="workflow_info" 
                        label="Status procesu" 
                        sortBy="workflow_status"
                        render={() => <WorkflowInfoField />}
                    />
                </DatagridConfigurable>
            </div>
        </StyledList>
    );
};

export default {
    list: BidderOfferArchivedList,
    hasList: true,
    hasShow: false,
    hasEdit: false,
    hasCreate: false,
};

